/**
 * StateManager and GameProvider
 * 
 * React Context-based state management for the visual novel game.
 * Provides GameState and methods to update it throughout the application.
 */

import { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect, ReactNode } from 'react';
import { GameState, ChoiceOption, Scenario, Scene, CommandItem, ClickableScreenZone } from '../types';
import { evaluateCondition } from './condition-evaluator';
import {
  flushLiveMissionToCampaign,
  readLiveCategoryCounts,
  resetLiveCategoryCounts,
  snapshotChapterCategoryCounts,
} from './peda-shield-stats';
import {
  flushLiveMissionTrustToCampaign,
  readLiveTrustCounts,
  resetLiveTrustCounts,
} from './ally-trust';
import { SaveLoadManager, SaveResult } from './save-load-manager';
import { SceneExecutor, SceneProgress } from './scene-executor';
import { resolveNextSceneId } from './scene-flow';
import { clickableZoneToChoiceOption } from './clickable-screen-utils';
import { AchievementManager } from './achievement-manager';
import {
  buildInventorySectionViews,
  hasScenarioInventory,
  markInventoryEntryOpened,
  syncInventoryCampaignStore,
  type InventoryCampaignStore,
  type InventorySectionView,
} from './inventory-manager';
import { useAuth } from './auth-context';
import { apiFetch } from './api-client';
import { completePreviousMissionForScene, getMissionStartScene, syncMissionCompleted } from './mission-progress';
import { reportScormScenarioComplete } from './scorm-bridge';
import { reportMissionResult } from './mission-results';
import { bindPlaytimeActivityListeners, getActivePlaytimeIncrementSeconds } from './playtime-idle';
import { resolveTextPlaceholders } from '../utils/text-placeholders';
import { snapshotChapterConfidence } from './confidence-utils';

export interface GameContextValue {
  // State
  gameState: GameState | null;
  scenario: Scenario | null;
  currentScene: Scene | null;
  isLoading: boolean;
  error: string | null;
  saveResult: SaveResult | null;
  
  // State management methods
  initializeGame: (scenarioId: string, scenario: Scenario, options?: { force?: boolean; startScene?: string; missionId?: string }) => void;
  loadSavedGame: (scenarioId: string, scenario: Scenario, missionId?: string) => boolean;
  advanceDialogue: () => void;
  makeChoice: (option: ChoiceOption) => void;
  makeClickableZoneChoice: (zone: ClickableScreenZone) => void;
  setFlag: (name: string, value: boolean) => void;
  setVariable: (name: string, value: number) => void;
  collectItem: (itemId: string) => void;
  navigateToScene: (sceneId: string) => void;
  navigateToChapter: (chapterId: string) => void;
  resetGame: () => void;
  clearSaveResult: () => void;
  setOnBackToMenu: (callback: (() => void) | null) => void;
  
  // Save/Load
  saveGame: () => SaveResult;
  /** Marks the scenario as completed and persists to save (so re-entry shows end screen) */
  markScenarioCompleted: () => void;
  /** Clears scenarioCompleted when we're mid-story (e.g. confidence gain with gotoScene) so save is consistent */
  clearScenarioCompleted: () => void;
  
  // Achievement System
  getAchievementProgress: () => { unlocked: number; total: number };
  unlockAchievement: (achievementId: string) => void;
  setAchievementAnimating: (isAnimating: boolean) => void;
  
  // Skill System
  unlockSkill: (skillId: string) => void;
  getSkillProgress: () => { unlocked: number; total: number };
  
  // Confidence System
  setConfidence: (characterId: string, level: number) => void;
  getConfidence: (characterId: string) => number;
  
  // Utility
  evaluateCondition: (condition: string) => boolean;
  applyCommands: (commands: CommandItem[]) => void;
  getCurrentContent: () => any | null;
  hasMoreContent: () => boolean;
  isAtChoice: () => boolean;
  getSceneProgress: () => SceneProgress;
  interpolateText: (text: string) => string;

  /** Campaign inventory unlocks / opened state (metadata.inventory). */
  inventoryStore: InventoryCampaignStore;
  getInventorySections: () => InventorySectionView[];
  markInventoryOpened: (entryId: string) => void;
  hasInventoryCatalog: boolean;
}

const GameContext = createContext<GameContextValue | null>(null);

/**
 * Hook to access game context
 */
export function useGame(): GameContextValue {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}

interface GameProviderProps {
  children: ReactNode;
  /** When false, session does not read or write local saves (studio preview). */
  persistSaves?: boolean;
}

/**
 * Creates initial game state
 */
function isChapterStartScene(scenario: Scenario, sceneId: string): boolean {
  return scenario.metadata.chapters?.some((chapter) => chapter.startScene === sceneId) ?? false;
}

function getChapterForStartScene(scenario: Scenario, sceneId: string) {
  return scenario.metadata.chapters?.find((chapter) => chapter.startScene === sceneId);
}

function resolveMissionPedaShieldsMax(scenario: Scenario, missionId?: string): number {
  if (missionId) {
    const mission = scenario.metadata.missions?.find((entry) => entry.id === missionId);
    if (mission?.pedaShieldsMax != null) {
      return mission.pedaShieldsMax;
    }
  }
  return scenario.metadata.initialVariables?.peda_shields_max ?? 0;
}

function applyMissionShieldSettings(
  scenario: Scenario,
  variables: Record<string, number>,
  missionId?: string
): Record<string, number> {
  return {
    ...variables,
    peda_shields_max: resolveMissionPedaShieldsMax(scenario, missionId),
  };
}

function snapshotChapterShields(variables: Record<string, number>): Record<string, number> {
  const currentShields = variables.peda_shields ?? 0;
  return {
    ...variables,
    peda_shields_chapter_start: currentShields,
  };
}

function snapshotChapterStarts(
  variables: Record<string, number>,
  characterIds: string[],
): Record<string, number> {
  return snapshotChapterConfidence(
    snapshotChapterCategoryCounts(snapshotChapterShields(variables)),
    characterIds,
  );
}

function createInitialGameState(
  scenarioId: string,
  startSceneId: string,
  scenario: Scenario,
  missionId?: string
): GameState {
  const baseVariables = { ...(scenario.metadata.initialVariables ?? {}) };
  const characterIds = (scenario.metadata.characters ?? []).map((c) => c.id);
  const variables = snapshotChapterStarts(
    applyMissionShieldSettings(scenario, {
      ...baseVariables,
      peda_shields: baseVariables.peda_shields ?? 0,
    }, missionId),
    characterIds,
  );
  return {
    scenarioId,
    missionId,
    currentSceneId: startSceneId,
    flags: {},
    variables,
    inventory: [],
    visitedScenes: new Set([startSceneId]),
    unlockedAchievements: [],
    unlockedSkills: [],           // Initialize empty skills array
    hiddenElements: new Set(),     // Initialize empty hidden elements set
    isAchievementAnimating: false
  };
}

/**
 * GameProvider component
 * 
 * Consolidated state to prevent infinite re-render loops and race conditions.
 */
export function GameProvider({ children, persistSaves = true }: GameProviderProps): JSX.Element {
  const { user } = useAuth();
  const lastSyncTimeRef = useRef<number>(Date.now());
  const lastActivityRef = useRef<number>(Date.now());
  const persistSavesRef = useRef(persistSaves);
  persistSavesRef.current = persistSaves;
  const userRef = useRef(user);
  userRef.current = user;

  const persistSave = useCallback((saveScenarioId: string, gameState: GameState): SaveResult => {
    if (!persistSavesRef.current) return { success: true };
    return SaveLoadManager.save(saveScenarioId, gameState);
  }, []);

  // Single source of truth for game state
  const [data, setData] = useState<{
    gameState: GameState | null;
    scenario: Scenario | null;
    currentScene: Scene | null;
    error: string | null;
    saveResult: SaveResult | null;
  }>({
    gameState: null,
    scenario: null,
    currentScene: null,
    error: null,
    saveResult: null,
  });

  const [inventoryStore, setInventoryStore] = useState<InventoryCampaignStore>({
    unlocked: [],
    opened: [],
  });
  
  // Use ref to avoid stale closures in callbacks
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => {
    const { gameState, scenario } = dataRef.current;
    if (!gameState || !scenario) {
      // Keep campaign unlocks in memory across resetGame() so a story relaunch
      // does not look like a batch of brand-new inventory pings.
      return;
    }
    if (!hasScenarioInventory(scenario.metadata.inventory)) {
      setInventoryStore({ unlocked: [], opened: [] });
      return;
    }
    const store = syncInventoryCampaignStore(
      gameState.scenarioId,
      scenario.metadata.inventory,
      gameState,
    );
    setInventoryStore(store);
  }, [data.gameState, data.scenario]);

  /** Track user interaction so playtime pauses after prolonged inactivity. */
  useEffect(() => {
    if (!data.gameState || !persistSavesRef.current) return;

    return bindPlaytimeActivityListeners(() => {
      lastActivityRef.current = Date.now();
    });
  }, [data.gameState]);

  /**
   * Sends the current scene and the playtime since the last sync to the backend.
   * `keepalive` lets the request finish while the page is being closed.
   */
  const syncStats = useCallback(async (options: { keepalive?: boolean } = {}) => {
    const { gameState, scenario } = dataRef.current;
    if (!userRef.current || !gameState || !scenario || !persistSavesRef.current) return;

    const now = Date.now();
    const previousSync = lastSyncTimeRef.current;
    const playtimeIncrement = getActivePlaytimeIncrementSeconds(
      previousSync,
      lastActivityRef.current,
      now
    );
    // Claim this time slice before awaiting so overlapping syncs never count it twice.
    lastSyncTimeRef.current = now;

    const progress = SceneExecutor.calculateProgress(scenario, gameState.currentSceneId);

    try {
      await apiFetch('/api/stats/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          scenario_id: gameState.scenarioId,
          playtime_increment: playtimeIncrement,
          progress_percent: Math.round(progress.percentage),
          last_scene_id: gameState.currentSceneId
        }),
        keepalive: options.keepalive,
      });
    } catch (error) {
      if (lastSyncTimeRef.current === now) {
        lastSyncTimeRef.current = previousSync;
      }
      console.error('Failed to sync stats:', error);
    }
  }, []);

  const activeScenarioId = data.gameState?.scenarioId ?? null;
  // auth-context swaps the user object on every focus; only a different account should restart the timer.
  const syncUserId = user?.id ?? null;

  /**
   * Periodically sync game stats to backend, and once more when the page is hidden or closed.
   * Depends on the scenario only, so changing scene does not restart the timer.
   */
  useEffect(() => {
    if (!syncUserId || !activeScenarioId || !persistSavesRef.current) return;

    const syncInterval = setInterval(() => {
      void syncStats();
    }, 30000); // Every 30 seconds

    const flush = () => {
      void syncStats({ keepalive: true });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', flush);

    return () => {
      clearInterval(syncInterval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', flush);
    };
  }, [syncUserId, activeScenarioId, syncStats]);
  
  // Store callback for back to menu navigation
  const onBackToMenuRef = useRef<(() => void) | null>(null);

  /**
   * Internal helper to update current scene when state changes
   */
  const syncScene = useCallback((gameState: GameState, scenario: Scenario) => {
    if (!gameState.currentSceneId) return null;
    return SceneExecutor.getSceneById(scenario, gameState.currentSceneId) || null;
  }, []);

  /** Map old scene IDs to new ones for backward compatibility with save data */
  const migrateScenePath = useCallback((path: string[]): string[] => {
    const map: Record<string, string> = {
      chapter1_phase1_d1: 'chapter1_phase1_couloir',
      chapter1_phase1_d2: 'chapter1_phase1_machine',
      chapter1_phase1_d3: 'chapter1_phase1_common2',
      // darwinx-signaux-faibles — S1.1 consolidated into s11_start (2025 refactor)
      s11_ignore: 's11_start',
      s11_voix1: 's11_start',
      s11_choix1: 's11_start',
      s11_non1: 's11_start',
      s11_non1_choix: 's11_start',
      s11_non2: 's11_start',
      s11_non2_choix: 's11_start',
      s11_non3: 's11_start',
      s11_non3_choix: 's11_start',
      s11_oui_fin: 's11_after_call',
    };
    return path.map(id => map[id] ?? id);
  }, []);

  const resolveSceneFromSave = useCallback(
    (loadedState: GameState, scenario: Scenario): { state: GameState; scene: Scene | null } => {
      let state = loadedState;
      let scene = syncScene(state, scenario);

      if (!scene && (state as GameState & { currentScenePath?: string[] }).currentScenePath?.length) {
        const legacyPath = migrateScenePath(
          (state as GameState & { currentScenePath: string[] }).currentScenePath
        );
        const sceneId = legacyPath[legacyPath.length - 1]!;
        state = { ...state, currentSceneId: sceneId };
        scene = syncScene(state, scenario);
      }

      if (!scene) {
        const missionStartScene = state.missionId
          ? getMissionStartScene(scenario, state.missionId)
          : undefined;
        const startSceneId = missionStartScene ?? scenario.metadata.startScene;
        state = {
          ...state,
          currentSceneId: startSceneId,
        };
        scene = syncScene(state, scenario);
      }

      if (scene) {
        state = SceneExecutor.applyLeadingCommands(scenario, state);
        scene = syncScene(state, scenario);
      }

      return { state, scene };
    },
    [syncScene, migrateScenePath]
  );

  /**
   * Internal helper to evaluate achievements and update state
   */
  const evaluateAchievements = useCallback((gameState: GameState, scenario: Scenario): GameState => {
    const newlyUnlocked = AchievementManager.evaluateAchievements(
      scenario.metadata.achievements,
      gameState
    );

    if (newlyUnlocked.length > 0) {
      return {
        ...gameState,
        unlockedAchievements: [
          ...gameState.unlockedAchievements,
          ...newlyUnlocked.map(a => a.id)
        ],
        isAchievementAnimating: true,
        lastUnlockedAchievementId: newlyUnlocked[0]!.id
      };
    }

    return gameState;
  }, []);

  /**
   * Initializes a new game
   */
  const initializeGame = useCallback((
    scenarioId: string,
    newScenario: Scenario,
    options?: { force?: boolean; startScene?: string; missionId?: string }
  ) => {
    const force = options?.force === true;
    const missionId = options?.missionId;
    const startSceneId = options?.startScene ?? newScenario.metadata.startScene;

    // Skip if already initialized for this scenario/mission, unless force is true
    if (!force &&
        dataRef.current.gameState?.scenarioId === scenarioId &&
        dataRef.current.gameState?.missionId === missionId &&
        dataRef.current.scenario === newScenario) {
      return;
    }

    let initialState = createInitialGameState(
      scenarioId,
      startSceneId,
      newScenario,
      missionId
    );
    let scene = syncScene(initialState, newScenario);

    const now = Date.now();
    lastSyncTimeRef.current = now;
    lastActivityRef.current = now;

    if (scene) {
      initialState = SceneExecutor.applyLeadingCommands(newScenario, initialState);
      scene = syncScene(initialState, newScenario);
    }
    
    setData(prev => ({
      ...prev,
      scenario: newScenario,
      gameState: initialState,
      currentScene: scene,
      error: scene ? null : `Scene not found: ${startSceneId}`
    }));
  }, [syncScene]);

  /**
   * Loads a saved game
   */
  const loadSavedGame = useCallback((scenarioId: string, newScenario: Scenario, missionId?: string): boolean => {
    if (!persistSavesRef.current) return false;

    // Skip if already loaded for this scenario/mission with a valid scene
    if (dataRef.current.gameState?.scenarioId === scenarioId &&
        dataRef.current.gameState?.missionId === missionId &&
        dataRef.current.scenario === newScenario &&
        dataRef.current.currentScene) {
      return true;
    }

    const loadResult = SaveLoadManager.load(scenarioId, missionId);
    
    if (loadResult.success && loadResult.data) {
      let loadedState = SaveLoadManager.saveDataToGameState(loadResult.data);
      if (missionId && loadedState.missionId && loadedState.missionId !== missionId) {
        return false;
      }
      if (missionId && !loadedState.missionId) {
        loadedState = { ...loadedState, missionId };
      }
      const { state, scene } = resolveSceneFromSave(loadedState, newScenario);
      loadedState = state;

      const now = Date.now();
      lastSyncTimeRef.current = now;
      lastActivityRef.current = now;

      setData(prev => ({
        ...prev,
        scenario: newScenario,
        gameState: loadedState,
        currentScene: scene,
        error: scene ? null : `Scene not found in save data`
      }));
      return true;
    }

    return false;
  }, [resolveSceneFromSave]);

  /**
   * Interpolate @placeholders and {varName} in displayed text.
   */
  const interpolateText = useCallback((text: string): string => {
    const { gameState, scenario } = dataRef.current;
    const withPlaceholders = resolveTextPlaceholders(
      text,
      scenario?.metadata.textPlaceholders
    );
    if (!gameState) return withPlaceholders;

    return withPlaceholders.replace(/\{(\w+)\}/g, (match, varName) => {
      if (varName in gameState.variables) {
        return String(gameState.variables[varName]);
      }
      if (varName in gameState.flags) {
        return String(gameState.flags[varName]);
      }
      return match;
    });
  }, []);

  /**
   * Advances to the next dialogue/content item
   */
  const advanceDialogue = useCallback(() => {
    setData(prev => {
      const { gameState, scenario } = prev;
      if (!gameState || !scenario) return prev;

      const result = SceneExecutor.advanceScene(scenario, gameState);
      const updatedState = evaluateAchievements(result.gameState, scenario);

      return {
        ...prev,
        gameState: updatedState,
        currentScene: result.scene,
      };
    });
  }, [evaluateAchievements]);

  /**
   * Makes a choice and navigates to the target scene
   */
  const applyChoiceOption = useCallback((option: ChoiceOption, sceneId: string) => {
    const { gameState, scenario } = dataRef.current;

    if (!gameState || !scenario) return;

    if (option.goto === '__MAIN_MENU__' || option.goto === '__MENU__') {
      if (option.actions && option.actions.length > 0) {
        SceneExecutor.executeCommands(option.actions, gameState);
      }
      if (gameState.missionId) {
        void syncMissionCompleted(gameState.scenarioId, gameState.missionId);
      }
      void syncStats();
      setData(prev => {
        if (!prev.gameState) return prev;
        const newState = { ...prev.gameState, scenarioCompleted: true };
        const saveRes = persistSave(prev.gameState.scenarioId, newState);
        if (saveRes.cleanedUpSaves && saveRes.cleanedUpSaves.length > 0) {
          setTimeout(() => setData(p => ({ ...p, saveResult: saveRes })), 0);
        }
        return { ...prev, gameState: newState };
      });
      return;
    }

    const result = SceneExecutor.processChoice(scenario, gameState, option, sceneId);

    if ('error' in result) {
      setData(prev => ({ ...prev, error: result.error }));
      return;
    }

    const updatedState = evaluateAchievements(result.gameState, scenario);
    const targetSceneId = updatedState.currentSceneId;
    if (scenario.metadata.missions?.length) {
      completePreviousMissionForScene(
        updatedState.scenarioId,
        scenario.metadata.missions,
        scenario.metadata.chapters,
        targetSceneId
      );
    }

    setData(prev => ({
      ...prev,
      scenario,
      gameState: updatedState,
      currentScene: result.scene,
      error: null
    }));

    setTimeout(() => {
      if (updatedState) {
        const saveRes = persistSave(updatedState.scenarioId, updatedState);
        if (saveRes.cleanedUpSaves && saveRes.cleanedUpSaves.length > 0) {
          setData(prev => ({ ...prev, saveResult: saveRes }));
        }
      }
    }, 100);
  }, [evaluateAchievements, syncStats]);

  const makeChoice = useCallback((option: ChoiceOption) => {
    const { gameState } = dataRef.current;
    if (!gameState) return;
    applyChoiceOption(option, gameState.currentSceneId);
  }, [applyChoiceOption]);

  const makeClickableZoneChoice = useCallback((zone: ClickableScreenZone) => {
    const { gameState } = dataRef.current;
    if (!gameState) return;
    applyChoiceOption(clickableZoneToChoiceOption(zone), gameState.currentSceneId);
  }, [applyChoiceOption]);

  /**
   * Sets a flag value
   */
  const setFlag = useCallback((name: string, value: boolean) => {
    const { scenario, gameState } = dataRef.current;
    if (!scenario || !gameState) return;

    const newState = {
      ...gameState,
      flags: { ...gameState.flags, [name]: value }
    };
    const nextData = {
      ...dataRef.current,
      gameState: evaluateAchievements(newState, scenario)
    };
    dataRef.current = nextData;
    setData(nextData);
  }, [evaluateAchievements]);

  /**
   * Sets a variable value
   */
  const setVariable = useCallback((name: string, value: number) => {
    const { scenario } = dataRef.current;
    if (!scenario) return;

    setData(prev => {
      if (!prev.gameState) return prev;
      const newState = {
        ...prev.gameState,
        variables: { ...prev.gameState.variables, [name]: value }
      };
      return {
        ...prev,
        gameState: evaluateAchievements(newState, scenario)
      };
    });
  }, [evaluateAchievements]);

  /**
   * Sets confidence level for a character
   */
  const setConfidence = useCallback((characterId: string, level: number) => {
    const { scenario } = dataRef.current;
    if (!scenario) return;

    setData(prev => {
      if (!prev.gameState) return prev;
      const newState = {
        ...prev.gameState,
        variables: { 
          ...prev.gameState.variables, 
          [`${characterId}_confidence`]: level 
        }
      };
      return {
        ...prev,
        gameState: evaluateAchievements(newState, scenario)
      };
    });
  }, [evaluateAchievements]);

  /**
   * Gets confidence level for a character
   */
  const getConfidence = useCallback((characterId: string): number => {
    const { gameState } = dataRef.current;
    if (!gameState) return 0;
    return gameState.variables[`${characterId}_confidence`] || 0;
  }, []);

  /**
   * Unlocks an achievement manually
   */
  const unlockAchievement = useCallback((achievementId: string) => {
    const { scenario } = dataRef.current;
    if (!scenario) return;

    setData(prev => {
      if (!prev.gameState || (prev.gameState.unlockedAchievements || []).includes(achievementId)) return prev;

      const newState = {
        ...prev.gameState,
        unlockedAchievements: [...prev.gameState.unlockedAchievements, achievementId],
        isAchievementAnimating: true,
        lastUnlockedAchievementId: achievementId
      };
      return {
        ...prev,
        gameState: evaluateAchievements(newState, scenario)
      };
    });
  }, [evaluateAchievements]);

  /**
   * Applies multiple commands to the game state
   */
  const applyCommands = useCallback((commands: CommandItem[]) => {
    const { scenario } = dataRef.current;
    if (!scenario) return;

    setData(prev => {
      if (!prev.gameState) return prev;
      const commandScenes = commands.map((command, index) => ({
        ...command,
        id: `__runtime_cmd_${index}`,
        type: 'command' as const,
      }));
      const newState = SceneExecutor.executeCommands(commandScenes, prev.gameState);
      return {
        ...prev,
        gameState: evaluateAchievements(newState, scenario)
      };
    });
  }, [evaluateAchievements]);

  /**
   * Collects an item
   */
  const collectItem = useCallback((itemId: string) => {
    const { scenario } = dataRef.current;
    if (!scenario) return;

    setData(prev => {
      if (!prev.gameState || (prev.gameState.inventory || []).includes(itemId)) return prev;
      const newState = {
        ...prev.gameState,
        inventory: [...(prev.gameState.inventory || []), itemId],
        flags: { ...(prev.gameState.flags || {}), [`has_${itemId}`]: true }
      };
      return {
        ...prev,
        gameState: evaluateAchievements(newState, scenario)
      };
    });
  }, [evaluateAchievements]);

  /**
   * Unlocks a skill by ID
   */
  const unlockSkill = useCallback((skillId: string) => {
    const { scenario } = dataRef.current;
    if (!scenario) return;

    setData(prev => {
      if (!prev.gameState || (prev.gameState.unlockedSkills || []).includes(skillId)) return prev;
      
      // Verify skill exists in scenario
      const skillExists = scenario.metadata.skills?.some(s => s.id === skillId);
      if (!skillExists) {
        console.warn(`Skill "${skillId}" not found in scenario`);
        return prev;
      }

      const newState = {
        ...prev.gameState,
        unlockedSkills: [...(prev.gameState.unlockedSkills || []), skillId]
      };
      return {
        ...prev,
        gameState: evaluateAchievements(newState, scenario)
      };
    });
  }, [evaluateAchievements]);

  /**
   * Navigates to a scene by ID
   */
  const navigateToScene = useCallback((sceneId: string) => {
    // Handle special scene IDs
    if (sceneId === '__MAIN_MENU__' || sceneId === '__MENU__') {
      if (onBackToMenuRef.current) {
        onBackToMenuRef.current();
      }
      return;
    }

    setData(prev => {
      const { scenario, gameState } = prev;
      if (!scenario || !gameState) return prev;

      const result = SceneExecutor.navigateToScene(scenario, gameState, sceneId);

      if ('error' in result) {
        return { ...prev, error: result.error };
      }

      const chapter = getChapterForStartScene(scenario, sceneId);
      const switchesMission = Boolean(
        chapter?.missionId && chapter.missionId !== gameState.missionId
      );
      let nextVariables = result.gameState.variables ?? {};
      let nextMissionId = result.gameState.missionId;

      if (switchesMission && chapter?.missionId) {
        const previousMissionId = gameState.missionId;
        const characterIds = (scenario.metadata.characters ?? []).map((character) => character.id);
        if (previousMissionId) {
          const liveCounts = readLiveCategoryCounts(gameState.variables ?? {});
          flushLiveMissionToCampaign(gameState.scenarioId, previousMissionId, liveCounts);
          const liveTrust = readLiveTrustCounts(gameState.variables ?? {}, characterIds);
          flushLiveMissionTrustToCampaign(gameState.scenarioId, previousMissionId, liveTrust);
          reportMissionResult({
            scenarioId: gameState.scenarioId,
            missionId: previousMissionId,
            liveCounts,
            liveTrust,
          });
        }
        nextMissionId = chapter.missionId;
        nextVariables = resetLiveTrustCounts(
          resetLiveCategoryCounts(
            applyMissionShieldSettings(scenario, {
              ...nextVariables,
              peda_shields: 0,
            }, chapter.missionId),
          ),
          characterIds,
        );
      }

      // Switching to a new mission means the scenario is no longer "completed":
      // clear the flag so the gameplay screen doesn't show the end screen.
      const scenarioCompleted = switchesMission ? false : result.gameState.scenarioCompleted;
      const characterIds = (scenario.metadata.characters ?? []).map((c) => c.id);
      const stateWithChapterSnapshot = isChapterStartScene(scenario, sceneId)
        ? {
            ...result.gameState,
            missionId: nextMissionId,
            scenarioCompleted,
            variables: snapshotChapterStarts(nextVariables, characterIds),
          }
        : {
            ...result.gameState,
            missionId: nextMissionId,
            scenarioCompleted,
            variables: nextVariables,
          };
      const updatedState = evaluateAchievements(stateWithChapterSnapshot, scenario);
      if (scenario.metadata.missions?.length) {
        completePreviousMissionForScene(
          updatedState.scenarioId,
          scenario.metadata.missions,
          scenario.metadata.chapters,
          sceneId
        );
      }

      return {
        ...prev,
        scenario,
        gameState: updatedState,
        currentScene: result.scene,
        error: null
      };
    });
  }, [evaluateAchievements]);

  /**
   * Navigates to a chapter by starting its first scene
   */
  const navigateToChapter = useCallback((chapterId: string) => {
    setData(prev => {
      const { scenario, gameState } = prev;
      if (!scenario || !gameState || !scenario.metadata.chapters) return prev;

      const chapter = scenario.metadata.chapters.find(c => c.id === chapterId);
      if (!chapter) {
        console.warn(`Chapter "${chapterId}" not found`);
        return prev;
      }

      const result = SceneExecutor.navigateToScene(scenario, gameState, chapter.startScene);
      if ('error' in result) {
        return { ...prev, error: result.error };
      }

      const initialShields = scenario.metadata.initialVariables?.peda_shields ?? 0;
      const characterIds = (scenario.metadata.characters ?? []).map((c) => c.id);
      const updatedState = evaluateAchievements({
        ...result.gameState,
        variables: snapshotChapterStarts({
          ...result.gameState.variables,
          peda_shields: initialShields,
        }, characterIds),
      }, scenario);

      return {
        ...prev,
        scenario,
        gameState: updatedState,
        currentScene: result.scene,
        error: null,
      };
    });
  }, [evaluateAchievements]);

  /**
   * Resets the game state
   */
  const resetGame = useCallback(() => {
    // Last scenes since the periodic sync would be lost otherwise.
    void syncStats();
    setData(prev => ({
      ...prev,
      gameState: null,
      scenario: null,
      currentScene: null,
      error: null,
    }));
  }, [syncStats]);

  /**
   * Saves the current game
   */
  const saveGame = useCallback((): SaveResult => {
    const { gameState } = dataRef.current;
    
    if (!gameState) {
      return { success: false, error: 'No game state to save' };
    }
    
    const result = persistSave(gameState.scenarioId, gameState);
    if (result.cleanedUpSaves && result.cleanedUpSaves.length > 0) {
      setData(prev => ({ ...prev, saveResult: result }));
    }
    return result;
  }, []);

  /**
   * Marks the scenario as completed and saves (so re-opening the story shows end screen).
   */
  const markScenarioCompleted = useCallback(() => {
    void syncStats();
    const completedScenarioId = dataRef.current.gameState?.scenarioId;
    if (completedScenarioId) reportScormScenarioComplete(completedScenarioId);
    setData(prev => {
      if (!prev.gameState) return prev;
      if (prev.gameState.missionId) {
        void syncMissionCompleted(prev.gameState.scenarioId, prev.gameState.missionId);
      }
      const newState = { ...prev.gameState, scenarioCompleted: true };
      const saveRes = persistSave(prev.gameState.scenarioId, newState);
      if (saveRes.cleanedUpSaves && saveRes.cleanedUpSaves.length > 0) {
        setTimeout(() => setData(p => ({ ...p, saveResult: saveRes })), 0);
      }
      return { ...prev, gameState: newState };
    });
  }, [syncStats]);

  const clearScenarioCompleted = useCallback(() => {
    setData(prev => {
      if (!prev.gameState || !prev.gameState.scenarioCompleted) return prev;
      const newState = { ...prev.gameState, scenarioCompleted: false };
      persistSave(prev.gameState.scenarioId, newState);
      return { ...prev, gameState: newState };
    });
  }, []);

  /**
   * Clears the last save result
   */
  const clearSaveResult = useCallback(() => {
    setData(prev => ({ ...prev, saveResult: null }));
  }, []);

  /**
   * Sets the callback for back to menu navigation
   */
  const setOnBackToMenu = useCallback((callback: (() => void) | null) => {
    onBackToMenuRef.current = callback;
  }, []);

  /**
   * Evaluates a condition against current game state
   */
  const evaluateConditionWrapper = useCallback((condition: string): boolean => {
    const { gameState } = dataRef.current;
    if (!gameState) return false;
    
    try {
      return evaluateCondition(condition, gameState);
    } catch (error) {
      console.error('Error evaluating condition:', condition, error);
      return false;
    }
  }, []);

  /**
   * Gets the current content item
   */
  const getCurrentContent = useCallback(() => {
    return dataRef.current.currentScene;
  }, []);

  const hasMoreContent = useCallback((): boolean => {
    const { gameState, scenario } = dataRef.current;
    if (!gameState || !scenario) return false;
    return resolveNextSceneId(scenario, gameState.currentSceneId) !== null;
  }, []);

  /**
   * Checks if current item is a choice
   */
  const isAtChoice = useCallback((): boolean => {
    const content = getCurrentContent();
    return SceneExecutor.isChoice(content);
  }, [getCurrentContent]);

  /**
   * Gets the current scene progress
   */
  const getSceneProgress = useCallback((): SceneProgress => {
    const { gameState, scenario } = dataRef.current;
    if (!gameState || !scenario) {
      return { currentIndex: 0, totalItems: 0, percentage: 0 };
    }
    return SceneExecutor.calculateProgress(scenario, gameState.currentSceneId);
  }, []);

  /**
   * Gets achievement progress
   */
  const getAchievementProgress = useCallback(() => {
    const { gameState, scenario } = dataRef.current;
    if (!gameState || !scenario) {
      return { unlocked: 0, total: 0 };
    }
    return AchievementManager.getProgress(scenario.metadata.achievements, gameState);
  }, []);

  /**
   * Gets skill progress
   */
  const getSkillProgress = useCallback(() => {
    const { gameState, scenario } = dataRef.current;
    if (!gameState || !scenario) {
      return { unlocked: 0, total: 0 };
    }
    return {
      unlocked: (gameState.unlockedSkills || []).length,
      total: (scenario.metadata.skills || []).length
    };
  }, []);

  /**
   * Sets achievement animating flag
   */
  const setAchievementAnimating = useCallback((isAnimating: boolean) => {
    setData(prev => {
      if (!prev.gameState) return prev;
      return {
        ...prev,
        gameState: {
          ...prev.gameState,
          isAchievementAnimating: isAnimating
        }
      };
    });
  }, []);

  const markInventoryOpened = useCallback((entryId: string) => {
    const { gameState } = dataRef.current;
    if (!gameState) return;
    const store = markInventoryEntryOpened(gameState.scenarioId, entryId);
    setInventoryStore(store);
  }, []);

  const getInventorySections = useCallback((): InventorySectionView[] => {
    const { gameState, scenario } = dataRef.current;
    if (!gameState || !scenario?.metadata.inventory) return [];
    return buildInventorySectionViews(
      scenario.metadata.inventory,
      gameState,
      inventoryStore,
      {
        scenes: scenario.scenes,
        characters: scenario.metadata.characters,
      },
    );
  }, [inventoryStore]);

  const hasInventoryCatalog = useMemo(
    () => hasScenarioInventory(data.scenario?.metadata.inventory),
    [data.scenario?.metadata.inventory],
  );

  // Final context value - memoized to prevent re-renders when data hasn't changed
  const contextValue: GameContextValue = useMemo(() => ({
    gameState: data.gameState,
    scenario: data.scenario,
    currentScene: data.currentScene,
    isLoading: false,
    error: data.error,
    saveResult: data.saveResult,
    initializeGame,
    loadSavedGame,
    advanceDialogue,
    makeChoice,
    makeClickableZoneChoice,
    setFlag,
    setVariable,
    collectItem,
    navigateToScene,
    navigateToChapter,
    resetGame,
    saveGame,
    markScenarioCompleted,
    clearScenarioCompleted,
    getAchievementProgress,
    unlockAchievement,
    setAchievementAnimating,
    unlockSkill,
    getSkillProgress,
    setConfidence,
    getConfidence,
    evaluateCondition: evaluateConditionWrapper,
    applyCommands,
    getCurrentContent,
    hasMoreContent,
    isAtChoice,
    getSceneProgress,
    clearSaveResult,
    interpolateText,
    setOnBackToMenu,
    inventoryStore,
    getInventorySections,
    markInventoryOpened,
    hasInventoryCatalog,
  }), [
    data.gameState,
    data.scenario,
    data.currentScene,
    data.error,
    data.saveResult,
    initializeGame,
    loadSavedGame,
    advanceDialogue,
    makeChoice,
    makeClickableZoneChoice,
    setFlag,
    setVariable,
    collectItem,
    navigateToScene,
    navigateToChapter,
    resetGame,
    saveGame,
    markScenarioCompleted,
    clearScenarioCompleted,
    getAchievementProgress,
    unlockAchievement,
    setAchievementAnimating,
    unlockSkill,
    getSkillProgress,
    setConfidence,
    getConfidence,
    evaluateConditionWrapper,
    applyCommands,
    getCurrentContent,
    hasMoreContent,
    isAtChoice,
    getSceneProgress,
    clearSaveResult,
    interpolateText,
    setOnBackToMenu,
    inventoryStore,
    getInventorySections,
    markInventoryOpened,
    hasInventoryCatalog,
  ]);

  return (
    <GameContext.Provider value={contextValue}>
      {children}
    </GameContext.Provider>
  );
}
